import { useCallback } from 'react'
import { useKg } from '@/features/design-system/kg-provider'
import { useStudioStore } from '@/store/studio-store'
import { toast } from '@/store/toast-store'
import type { ChannelDispatchStatus } from '@/store/ide-v2-slice'
import type { ChannelInput, ChannelOutput } from '../_shared/channel-types'
import { recordGenerationRun } from '../_shared/generation-run-stats'
import type { ChatMessage } from './chat-message'
import { routeChannel } from './route-channel'
import { humanizeGenerationSource } from './humanize-provenance'
import { SHACL_FAIL_PREFIX } from '@/shell/ide-v2/components/miss-reason-copy'

let activeChannelDispatchController: AbortController | null = null

type RenderedChannelOutput = Extract<ChannelOutput, { kind: 'rendered' }>
type AgentWorklogMessage = Extract<ChatMessage, { kind: 'agent-worklog' }>

export function abortActiveChannelDispatch(): boolean {
  const controller = activeChannelDispatchController
  if (!controller) return false

  const store = useStudioStore.getState()
  const current = store.channelDispatchStatus
  controller.abort()
  activeChannelDispatchController = null
  store.setDispatchedOutput({ kind: 'miss', reason: 'aborted' })
  store.replacePageProgress(null)
  store.setChannelDispatchStatus(
    makeDispatchStatus('aborted', current.mode, current.startedAt, 'Generation stopped.'),
  )
  return true
}

export interface UseChannelDispatch {
  isReady: boolean
  isRunning: boolean
  /**
   * Run an intent through Channel 1 (corpus lookup) then Channel 2 (generation).
   * Takes the intent and nothing else on purpose: the render it produces
   * REPLACES the project (`applyRenderedOutputToProject` below), so there is no
   * element or block for a caller to scope it to. Callers holding a creator's
   * selection want the local-edit planner (`submitIntentChunkPlan`), which is
   * the only thing that reads a `RefinementContext`. This signature used to
   * accept one and drop it — see `_shared/channel-types.ts`.
   */
  dispatch: (intent: string, options?: ChannelDispatchOptions) => void
  abort: () => void
}

export interface ChannelDispatchOptions {
  onMessage?: (message: ChatMessage) => void
  onSettled?: (result: ChannelDispatchResult) => void
}

export interface ChannelDispatchResult {
  status: 'succeeded' | 'failed' | 'interrupted'
  text: string
  messageIds: string[]
  terminalMessageId: string | null
}

function makeDispatchStatus(
  phase: ChannelDispatchStatus['phase'],
  mode: ChannelDispatchStatus['mode'],
  startedAt: number | null,
  message: string | null,
): ChannelDispatchStatus {
  return {
    phase,
    mode,
    startedAt,
    updatedAt: Date.now(),
    message,
  }
}

function isRunningPhase(phase: ChannelDispatchStatus['phase']): boolean {
  return (
    phase === 'pattern-retrieval' ||
    phase === 'mock-generation' ||
    phase === 'real-generation' ||
    phase === 'rendering'
  )
}

function makeAgentWorklogMessage(
  title: string,
  stage: AgentWorklogMessage['stage'],
  entries: AgentWorklogMessage['entries'],
): AgentWorklogMessage {
  return {
    kind: 'agent-worklog',
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    title,
    stage,
    entries,
  }
}

function applyRenderedOutputToProject(out: RenderedChannelOutput): void {
  useStudioStore.setState((state) => {
    const currentName = state.currentProjectName ?? state.currentProject?.manifest?.name
    const currentAppId = state.currentProject?.manifest?.appId ?? state.currentProjectId ?? undefined
    state.currentProject = {
      manifest: {
        ...out.manifest,
        ...(currentAppId ? { appId: currentAppId } : {}),
        ...(currentName ? { name: currentName } : {}),
      },
      blocks: out.blocks,
      data: state.currentProject?.data ?? {},
      flows: state.currentProject?.flows ?? {},
      tokens: state.currentProject?.tokens,
      designSystem: state.currentProject?.designSystem,
      flowDiagram: state.currentProject?.flowDiagram,
      aboxElementMap: state.currentProject?.aboxElementMap,
    }
    state.isDirty = true
  })
}

export function useChannelDispatch(): UseChannelDispatch {
  const { repo, ready, flagEnabled, error } = useKg()
  const setDispatchedOutput = useStudioStore((s) => s.setDispatchedOutput)
  const appendChatMessage = useStudioStore((s) => s.appendChatMessage)
  const replacePageProgress = useStudioStore((s) => s.replacePageProgress)
  const channelDispatchStatus = useStudioStore((s) => s.channelDispatchStatus)
  const setChannelDispatchStatus = useStudioStore((s) => s.setChannelDispatchStatus)
  // Phase A P2 — Channel 2 real-backend opt-in (default off)
  const useRealChannel2 = useStudioStore((s) => s.useRealChannel2)
  const cogoBackendUrl = useStudioStore((s) => s.cogoBackendUrl)
  const realPipeline = useStudioStore((s) => s.realPipeline)

  const abort = useCallback(() => {
    abortActiveChannelDispatch()
  }, [])

  const dispatch = useCallback((intent: string, options?: ChannelDispatchOptions) => {
    if (!ready || !repo) {
      // Visible feedback instead of a silent no-op — callers that pre-gate on
      // `isReady` never reach this, but programmatic dispatches (e.g. Ask coogo
      // during a cold KG init) must not appear to be dead buttons. The
      // kg-provider degrades to {ready: true, repo: null} permanently on init
      // failure or with the flag off — only a true cold init may promise that
      // retrying will help.
      if (error) {
        toast.error('The assistant failed to start — reload the page to retry.')
      } else if (!flagEnabled) {
        toast.warning("The assistant isn't enabled in this workspace.")
      } else {
        toast.warning('Still loading the workspace — try again in a moment.')
      }
      options?.onSettled?.({
        status: 'failed',
        text: error
          ? 'The assistant failed to start — reload the page to retry.'
          : !flagEnabled
            ? "The assistant isn't enabled in this workspace."
            : 'Still loading the workspace — try again in a moment.',
        messageIds: [],
        terminalMessageId: null,
      })
      return
    }
    // F30 — engine work-log vocabulary (ABox / SHACL / KG / Channel / RAG) is
    // developer-persona-only. Read the persona imperatively at dispatch time via
    // the established getState() convention (matches abortActiveChannelDispatch
    // at line ~21) rather than subscribing — keeps the useCallback dep list
    // stable. Snapshot once so every append below shares one persona view; if a
    // future refactor moves the appends out of this closure the snapshot must be
    // re-taken here.
    const developer = useStudioStore.getState().studioPersona === 'developer'

    abort()
    const controller = new AbortController()
    activeChannelDispatchController = controller
    const startedAt = Date.now()
    const mode: ChannelDispatchStatus['mode'] = useRealChannel2 ? 'real' : 'mock'
    const emittedMessageIds: string[] = []
    const emitChatMessage = (message: ChatMessage) => {
      emittedMessageIds.push(message.id)
      appendChatMessage(message)
      options?.onMessage?.(message)
    }
    setChannelDispatchStatus(
      makeDispatchStatus('pattern-retrieval', mode, startedAt, 'Checking project ontology patterns.'),
    )

    const userMsg: ChatMessage = {
      kind: 'user',
      id: crypto.randomUUID(),
      text: intent,
      timestamp: Date.now(),
    }
    emitChatMessage(userMsg)
    // F30 — pure plumbing (route/ABox/SHACL/mode); no creator-actionable content
    // → hidden for creators, kept verbatim for developers.
    if (developer) {
      emitChatMessage(makeAgentWorklogMessage(
        'Dispatch provenance',
        'dispatch',
        [
          { label: 'route', value: 'Channel 1 lookup -> Channel 2 if miss' },
          { label: 'ABox', value: 'KG repository ready', tone: 'success' },
          { label: 'SHACL', value: 'validation bridge armed', tone: 'success' },
          { label: 'mode', value: mode },
        ],
      ))
    }

    const input: ChannelInput = {
      kind: 'intent',
      pattern: intent,
    }

    // Real-mode opts threaded into routeChannel → dispatchChannelTwo.
    // RouteChannelOpts extends DispatchTwoOpts so realMode/backendUrl/realPipeline
    // flow through automatically. When useRealChannel2 is false, opts are
    // unset → Channel 2 mock pipeline (existing behavior).
    const channelOpts = useRealChannel2
      ? {
          signal: controller.signal,
          realMode: true as const,
          backendUrl: cogoBackendUrl,
          realPipeline,
        }
      : { signal: controller.signal }

    void (async (): Promise<ChannelDispatchResult> => {
      let terminal: ChannelDispatchResult | null = null
      try {
        let aiStartEmitted = false
        for await (const out of routeChannel(input, repo, channelOpts)) {
          if (controller.signal.aborted) break
          setDispatchedOutput(out)

          if (out.kind === 'rendered' && out.channel === 1) {
            applyRenderedOutputToProject(out)
            replacePageProgress(null)
            // F30 — plumbing work-log (route/ABox/SHACL); developer-only.
            if (developer) {
              emitChatMessage(makeAgentWorklogMessage(
                'Agent work-log',
                'retrieval',
                [
                  { label: 'route', value: 'Channel 1 local pattern', tone: 'success' },
                  { label: 'ABox', value: out.exampleId ?? 'matched KG example' },
                  { label: 'SHACL', value: 'rendered output queued for validation' },
                ],
              ))
            }
            emitChatMessage({
              kind: 'ai-channel-1-hit',
              id: crypto.randomUUID(),
              timestamp: Date.now(),
              lookupMs: out.timings.lookupMs,
            })
            const successMessage: ChatMessage = {
              kind: 'ai-result-success',
              id: crypto.randomUUID(),
              timestamp: Date.now(),
              channel: 1,
              totalMs: out.timings.totalMs,
              stages: [
                { label: 'lookup', ms: out.timings.lookupMs },
                { label: 'bind', ms: out.timings.bindMs },
              ],
              speedStats: recordGenerationRun({
                source: 'channel-1',
                totalMs: out.timings.totalMs,
                stages: { lookup: out.timings.lookupMs, bind: out.timings.bindMs },
              }),
            }
            emitChatMessage(successMessage)
            terminal = {
              status: 'succeeded',
              text: 'Matched a project ontology pattern.',
              messageIds: [...emittedMessageIds],
              terminalMessageId: successMessage.id,
            }
            setChannelDispatchStatus(
              makeDispatchStatus('completed', mode, startedAt, 'Matched a project ontology pattern.'),
            )
            break
          }

          if (out.kind === 'compose-preview') {
            emitChatMessage({
              kind: 'compose-preview',
              id: crypto.randomUUID(),
              timestamp: Date.now(),
              plan: out.plan,
              candidates: out.candidates,
              bindPreview: out.bindPreview,
            })
            setChannelDispatchStatus(
              makeDispatchStatus(
                'pattern-retrieval',
                mode,
                startedAt,
                `Reviewing ${out.plan.readyCandidateCount} RAG candidate${out.plan.readyCandidateCount === 1 ? '' : 's'}.`,
              ),
            )
            continue
          }

          if (out.kind === 'streaming' && !aiStartEmitted) {
            // F30 — plumbing work-log (route/pages/ABox/SHACL); developer-only.
            if (developer) {
              emitChatMessage(makeAgentWorklogMessage(
                'Agent work-log',
                'generation',
                [
                  { label: 'route', value: 'Channel 2 streaming', tone: 'warning' },
                  { label: 'pages', value: String(out.pages.length) },
                  { label: 'ABox', value: 'KG corpus miss -> generated draft' },
                  { label: 'SHACL', value: 'validation summary will follow after render' },
                ],
              ))
            }
            emitChatMessage({
              kind: 'ai-channel-2-start',
              id: crypto.randomUUID(),
              timestamp: Date.now(),
              pageCount: out.pages.length,
              estimateMs: out.totalDurationEstimateMs ?? 30_000,
            })
            aiStartEmitted = true
          }

          if (out.kind === 'streaming') {
            setChannelDispatchStatus(
              makeDispatchStatus(
                mode === 'real' ? 'real-generation' : 'mock-generation',
                mode,
                startedAt,
                mode === 'real' ? 'Drafting with the real generator.' : 'Drafting with the mock generator.',
              ),
            )
            replacePageProgress({
              kind: 'page-progress',
              id: 'page-progress-frame',
              timestamp: Date.now(),
              pages: out.pages,
              elapsedMs: out.elapsedMs,
            })
            continue
          }

          if (out.kind === 'rendered' && out.channel === 2) {
            setChannelDispatchStatus(
              makeDispatchStatus('rendering', mode, startedAt, 'Rendering the generated result.'),
            )
            applyRenderedOutputToProject(out)
            replacePageProgress(null)
            // Real-mode generation provenance (backend meta.generationSource).
            // Shown even without a pick trace, so a zero-shot or older-backend
            // render still states honestly what produced it.
            if (out.provenance) {
              const isRetrieval = out.provenance.generationSource === 'retrieval'
              // F30 — provenance (reused-vs-fresh) IS a creator-meaningful trust
              // signal, so it is HUMANIZED rather than hidden: developers keep
              // the raw engineering copy + internal exampleId; creators get the
              // jargon-free phrasing and never see the exampleId.
              const entries = developer
                ? [
                    {
                      label: 'source',
                      value: isRetrieval
                        ? 'retrieval — bound a verified corpus example'
                        : 'zero-shot — LLM-generated (no corpus example)',
                      tone: (isRetrieval ? 'success' : 'warning') as 'success' | 'warning',
                    },
                    ...(out.provenance.exampleId
                      ? [{ label: 'example', value: out.provenance.exampleId }]
                      : []),
                  ]
                : [
                    {
                      label: 'source',
                      value: humanizeGenerationSource(out.provenance.generationSource),
                      tone: (isRetrieval ? 'success' : 'warning') as 'success' | 'warning',
                    },
                  ]
              emitChatMessage(makeAgentWorklogMessage('Generation provenance', 'provenance', entries))
            }
            const successMessage: ChatMessage = {
              kind: 'ai-result-success',
              id: crypto.randomUUID(),
              timestamp: Date.now(),
              channel: 2,
              totalMs: out.timings.totalMs,
              // generationMs is the only stage channel 2 measures today
              // (lookup/bind are hardwired 0 there — omitting beats lying).
              stages: out.timings.generationMs !== undefined
                ? [{ label: 'generation', ms: out.timings.generationMs }]
                : undefined,
              speedStats: recordGenerationRun({
                source: mode === 'real' ? 'channel-2-real' : 'channel-2-mock',
                totalMs: out.timings.totalMs,
                ...(out.timings.generationMs !== undefined
                  ? { stages: { generation: out.timings.generationMs } }
                  : {}),
              }),
            }
            emitChatMessage(successMessage)
            terminal = {
              status: 'succeeded',
              text: 'Generated and rendered a starting point.',
              messageIds: [...emittedMessageIds],
              terminalMessageId: successMessage.id,
            }
            setChannelDispatchStatus(
              makeDispatchStatus('completed', mode, startedAt, 'Generated and rendered a starting point.'),
            )
            break
          }

          if (out.kind === 'miss' || out.kind === 'shaclFail') {
            const reason = out.kind === 'miss'
              ? out.reason
              : `${SHACL_FAIL_PREFIX}${out.failedPageId ?? 'unknown'}`
            const details = out.kind === 'miss' ? out.details : undefined
            replacePageProgress(null)
            setChannelDispatchStatus(
              makeDispatchStatus('failed', mode, startedAt, reason),
            )
            const failureMessage: ChatMessage = {
              kind: 'ai-result-failure',
              id: crypto.randomUUID(),
              timestamp: Date.now(),
              reason,
              ...(details ? { details } : {}),
              // Lane 3 fix — bind THIS bubble to the dispatched intent + its
              // channel-router source so Retry re-runs the right request.
              intent,
              source: 'channel',
            }
            emitChatMessage(failureMessage)
            terminal = {
              status: 'failed',
              text: reason,
              messageIds: [...emittedMessageIds],
              terminalMessageId: failureMessage.id,
            }
            break
          }
        }
      } catch (err) {
        if (controller.signal.aborted) {
          // The terminal result below owns interruption settlement. Returning
          // here would skip the captured retry's terminal callback.
        } else {
          console.error('[channel-router] dispatch failed', err)
          const errDetail = err instanceof Error
            ? err.message.trim() || undefined
            : undefined
          setChannelDispatchStatus(
            makeDispatchStatus('failed', mode, startedAt, 'bind-throw'),
          )
          replacePageProgress(null)
          const failureMessage: ChatMessage = {
            kind: 'ai-result-failure',
            id: crypto.randomUUID(),
            timestamp: Date.now(),
            reason: 'bind-throw',
            ...(errDetail ? { details: errDetail } : {}),
            // Lane 3 fix — bind THIS bubble to the dispatched intent + its
            // channel-router source so Retry re-runs the right request.
            intent,
            source: 'channel',
          }
          emitChatMessage(failureMessage)
          terminal = {
            status: 'failed',
            text: 'bind-throw',
            messageIds: [...emittedMessageIds],
            terminalMessageId: failureMessage.id,
          }
        }
      } finally {
        if (activeChannelDispatchController === controller) {
          activeChannelDispatchController = null
          const current = useStudioStore.getState().channelDispatchStatus
          if (!controller.signal.aborted && isRunningPhase(current.phase)) {
            replacePageProgress(null)
            setChannelDispatchStatus(
              makeDispatchStatus('failed', mode, startedAt, 'Generation ended without a render.'),
            )
          }
        }
      }
      if (terminal) return terminal
      return {
        status: controller.signal.aborted ? 'interrupted' : 'failed',
        text: controller.signal.aborted
          ? 'Generation stopped.'
          : 'Generation ended without a render.',
        messageIds: [...emittedMessageIds],
        terminalMessageId: null,
      }
    })().then((result) => options?.onSettled?.(result))
  }, [
    ready,
    repo,
    flagEnabled,
    error,
    abort,
    setDispatchedOutput,
    appendChatMessage,
    replacePageProgress,
    setChannelDispatchStatus,
    useRealChannel2,
    cogoBackendUrl,
    realPipeline,
  ])

  return {
    isReady: ready,
    isRunning: isRunningPhase(channelDispatchStatus.phase),
    dispatch,
    abort,
  }
}
